<template>
  <div :class="{'colored-container': isSimpilfiedUser}" class="actor-detail__testimonials"
       v-if="(userHasClaimedActors || testimonials.received.length || testimonials.sent.length)">
    <h2 class="h2 tw-flex flex-row gap-2">{{ $t('testimonials_title') }}
      <div class="title-count" v-if="isSimpilfiedUser">
        {{ totalTestimonialsCount }}
      </div>
      <template v-if="userHasClaimedActors && hasEligibleConnections">
        <ds-button
          icon="plus" :label="$t('write_testimonial')"
          variant="rounded" @click="openTestimonialModal"
          size="small"
        />
      </template>
    </h2>

    <!-- Tabs -->
    <div class="testimonials-tabs">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'received' }"
        @click="setActiveTab('received')"
      >
        {{ $t('received') }} ({{ testimonials.received.length }})
      </button>
      <button
        v-if="isActorClaimee"
        class="tab-button"
        :class="{ active: activeTab === 'sent' }"
        @click="setActiveTab('sent')"
      >
        {{ $t('sent') }} ({{ testimonials.sent.length }})
      </button>
      <button
        v-if="isActorClaimee && testimonials.hidden.length > 0"
        class="tab-button"
        :class="{ active: activeTab === 'hidden' }"
        @click="setActiveTab('hidden')"
      >
        {{ $t('hidden') }} ({{ testimonials.hidden.length }})
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <Icon name="spinner" class="animate-spin"/>
      <span>{{ $t('loading') }}...</span>
    </div>

    <!-- No testimonials -->
    <div v-else-if="currentTabTestimonials.length === 0" class="empty-state">
      <Icon name="star-outline" class="empty-icon"/>
      <p>{{ getEmptyStateMessage() }}</p>
    </div>

    <!-- Testimonials list -->
    <div v-else>
      <div class="testimonials-grid">
        <div
          v-for="testimonial in currentTabTestimonials"
          :key="testimonial.id"
          class="testimonial-item"
          :class="{
            'pending-approval': testimonial.isPending,
            'hidden-testimonial': activeTab === 'hidden'
          }"
        >
          <div class="testimonial-header">
            <div class="testimonial-author">
              <h5 class="author-company">{{ testimonial.authorActor.name }}</h5>
              <span class="author-user">{{ testimonial.user.name }}</span>
              <span v-if="testimonial.relationshipDescription" class="relationship-description">
              {{ testimonial.relationshipDescription }}
            </span>
            </div>
            <div class="testimonial-rating" v-if="testimonial.rating">
              <div class="rating-stars">
                <Icon
                  v-for="star in 5"
                  :key="star"
                  :name="star <= testimonial.rating ? 'star' : 'star-outline'"
                  :class="star <= testimonial.rating ? 'star-filled' : 'star-empty'"
                />
              </div>
              <span class="rating-value">{{ testimonial.rating }}/5</span>
            </div>
          </div>

          <div class="testimonial-content">
            <p>"{{ testimonial.content }}"</p>

            <!-- Edit info -->
            <div v-if="testimonial.editCount > 0" class="edit-info">
              <Icon name="edit" size="sm"/>
              <span>{{ $t('testimonial_edit_count', { count: testimonial.editCount }) }}</span>
              <span v-if="testimonial.lastEditedAt" class="edit-date">
              · {{ $t('testimonial_last_edited', { date: formatDate(testimonial.lastEditedAt) }) }}
            </span>
              <span v-if="testimonial.isPending && testimonial.editCount > 0" class="edit-pending-approval">
              · {{ $t('requires_reapproval_after_edit') }}
            </span>
            </div>

            <!-- Receiver response -->
            <!--          <div v-if="testimonial.receiverResponse" class="receiver-response">
                        <div class="response-header">
                          <Icon name="reply" size="sm"/>
                          <span class="response-label">Response from {{ testimonial.targetActor.name }}</span>
                          <span class="response-date">{{ formatDate(testimonial.receiverResponseAt) }}</span>
                        </div>
                        <p class="response-content">"{{ testimonial.receiverResponse }}"</p>
                      </div>-->
          </div>

          <div class="testimonial-footer">
            <span class="testimonial-date">{{ formatDate(testimonial.createdAt) }}</span>

            <!-- Management actions -->
            <div class="testimonial-actions" v-if="canManageTestimonial(testimonial)">
              <!-- Approval actions for pending testimonials -->
              <template v-if="testimonial.isPending && isTargetActorOwner(testimonial)">
                <ds-button
                  variant="primary"
                  icon="check"
                  size="small"
                  @click="approveTestimonial(testimonial)"
                  :title="$t('testimonial_approve')"
                  class="approve-btn"
                  v-tooltip.top="$t('approve')"
                >
                </ds-button>
                <ds-button
                  variant="secondary"
                  icon="x"
                  size="small"
                  @click="rejectTestimonial(testimonial)"
                  :title="$t('testimonial_reject')"
                  class="reject-btn"
                  v-tooltip.top="$t('reject')"
                >
                </ds-button>
              </template>

              <!-- Regular actions for approved testimonials -->
              <template v-else>
                <ds-button
                  v-if="canEditTestimonial(testimonial)"
                  variant="icon"
                  icon="edit"
                  size="small"
                  @click="startEditTestimonial(testimonial)"
                  v-tooltip.top="$t('testimonial_edit')"
                />

                <ds-button
                  v-if="canRespondToTestimonial(testimonial)"
                  variant="icon"
                  icon="reply"
                  size="small"
                  @click="startResponseToTestimonial(testimonial)"
                  v-tooltip.top="$t('testimonial_response_add')"
                />

                <ds-button
                  v-if="canRespondToTestimonial(testimonial)"
                  variant="icon"
                  :icon="testimonial.isVisible ? 'eye-slash' : 'eye'"
                  size="small"
                  @click="toggleTestimonialVisibility(testimonial)"
                  v-tooltip.top="testimonial.isVisible ? $t('hide_from_profile') : $t('show_on_profile')"
                />

                <ds-button
                  variant="icon"
                  icon="trash"
                  size="small"
                  @click="confirmDeleteTestimonial(testimonial)"
                  v-tooltip.top="$t('testimonial_delete')"
                  class="delete-btn"
                />
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Show More/Less button -->
      <div v-if="hasMoreTestimonials" class="show-more-container">
        <ds-button
          :label="showMoreButtonLabel"
          variant="rounded-outline"
          @click="toggleShowMore"
          class="show-more-btn"
        />
      </div>
    </div>

    <!-- Edit Testimonial Modal -->
    <div v-if="editingTestimonial" class="modal-overlay" @click="cancelEdit">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">{{ $t('testimonial_edit') }}</h3>

        <form @submit.prevent="submitEdit" class="edit-form">
          <div class="form-group">
            <label class="form-label">{{ $t('create_testimonial_rating_label') }}</label>
            <div class="rating-input">
              <button
                v-for="star in 5"
                :key="star"
                type="button"
                class="star-button"
                :class="{ active: editForm.rating && editForm.rating >= star }"
                @click="setEditRating(star)"
              >
                <Icon :name="editForm.rating >= star ? 'star' : 'star-outline'" fill="black"/>
              </button>
              <button
                v-if="editForm.rating"
                type="button"
                class="clear-rating-btn"
                @click="setEditRating(null)"
              >
                {{ $t('create_testimonial_clear_rating') }}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('create_testimonial_content_label') }} *</label>
            <div class="content-guidance">
              {{ $t('testimonial_content_guidance') }}
            </div>
            <textarea
              v-model="editForm.content"
              :placeholder="$t('create_testimonial_content_placeholder')"
              rows="4"
              class="form-textarea"
              :class="{ 'invalid': !isEditContentValid && editForm.content.length > 0 }"
              required
            ></textarea>
            <div class="textarea-footer">
              <div class="character-count" :class="{
                'over-limit': editForm.content.length > 2000 || editForm.content.length < 10,
                'valid': editForm.content.length >= 10 && editForm.content.length <= 2000
              }">
                {{ editForm.content.length }}/2000
              </div>
            </div>
            <div v-if="editErrors.content || editContentValidationMessage" class="form-error">
              {{ editErrors.content ? editErrors.content[0] : editContentValidationMessage }}
            </div>
          </div>

          <div class="modal-actions">
            <ds-button
              type="button"
              variant="rounded-secondary"
              @click="cancelEdit"
              :disabled="editLoading"
              :label="$t('cancel')"
            />
            <ds-button
              type="submit"
              variant="rounded"
              :disabled="!canSubmitEdit || editLoading"
              :loading="editLoading"
              :label="$t('update')"
            />
          </div>
        </form>
      </div>
    </div>

    <!-- Response Modal -->
    <div v-if="respondingToTestimonial" class="modal-overlay" @click="cancelResponse">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">{{ $t('testimonial_response_add') }}</h3>

        <form @submit.prevent="submitResponse" class="response-form">
          <div class="form-group">
            <label class="form-label">{{ $t('your_response') }} *</label>
            <div class="help-text response-info">
              <Icon name="info" size="sm"/>
              {{ $t('response_creates_testimonial_info') }}
            </div>
            <textarea
              v-model="responseForm.content"
              :placeholder="$t('testimonial_response_placeholder')"
              rows="3"
              class="form-textarea"
              required
            ></textarea>
            <div class="textarea-footer">
              <div class="character-count" :class="{ 'over-limit': responseForm.content.length > 1000 }">
                {{ responseForm.content.length }}/1000
              </div>
            </div>
            <div v-if="responseErrors.response" class="form-error">
              {{ responseErrors.response[0] }}
            </div>
          </div>

          <div class="modal-actions">
            <ds-button
              type="button"
              variant="rounded-secondary"
              @click="cancelResponse"
              :disabled="responseLoading"
              :label="$t('cancel')"
            />
            <ds-button
              type="submit"
              variant="rounded"
              :disabled="!canSubmitResponse || responseLoading"
              :loading="responseLoading"
              :label="$t('add_response')"
            />
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingTestimonial" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">{{ $t('testimonial_delete') }}</h3>
        <p>{{ $t('testimonial_delete_confirm') }}</p>

        <div class="modal-actions">
          <ds-button
            variant="rounded-secondary"
            @click="cancelDelete"
            :disabled="deleteLoading"
            :label="$t('cancel')"
          />
          <ds-button
            variant="rounded-danger"
            @click="executeDelete"
            :loading="deleteLoading"
            :label="$t('delete')"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import {
    addTestimonialResponse,
    approveTestimonial,
    deleteTestimonial,
    getTestimonialsByActor,
    rejectTestimonial,
    updateTestimonial,
    updateTestimonialVisibility
  } from '../../../Domain/Actor/Api/testimonials'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import FiltersMixin from '@/util/FiltersMixin'
  import ReportMixin from '@/util/ReportMixin'
  import Icon from '@/components/Icon/Icon.vue'
  import ConfigMixin from '@/util/ConfigMixin'
  import UserMixin from '@/util/UserMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'

  export default defineComponent({
    name: 'SimplifiedActorDetailTestimonials',
    mixins: [TranslationsMixin, FiltersMixin, ReportMixin, ConfigMixin, UserMixin],
    components: {
      Icon
    },
    props: {
      actor: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        testimonials: {
          received: [],
          sent: [],
          hidden: []
        },
        activeTab: 'received',
        loading: false,

        // Edit testimonial state
        editingTestimonial: null,
        editForm: {
          content: '',
          rating: null
        },
        editErrors: {},
        editLoading: false,

        // Response state
        respondingToTestimonial: null,
        responseForm: {
          content: ''
        },
        responseErrors: {},
        responseLoading: false,

        // Delete state
        deletingTestimonial: null,
        deleteLoading: false,

        // Show more state
        showMoreReceived: false,
        showMoreSent: false,
        showMoreHidden: false
      }
    },
    computed: {
      config() {
        return this.$store.state.config
      },
      userHasClaimedActors() {
        // Simple check - in a real app, you'd check if user has any claimed actors
        // For now, just check if user is authenticated
        return this.profile?.claims && this.profile?.claims?.length > 0
      },
      userClaimedActors() {
        return this.profile.claims
      },
      hasEligibleConnections() {
        // Always show the button - the modal will handle checking eligible authors
        return true
      },
      totalTestimonialsCount() {
        return this.testimonials.received.length + this.testimonials.sent.length
      },
      currentTabTestimonials() {
        const allTestimonials = this.testimonials[this.activeTab] || []

        // In advanced view, show all testimonials
        if (!this.isSimpilfiedUser) {
          return allTestimonials
        }

        // In simplified view, show only first 3 testimonials unless "show more" is active
        const showMoreKey = `showMore${this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1)}`
        const showMore = this[showMoreKey]
        return showMore ? allTestimonials : allTestimonials.slice(0, 3)
      },
      hasMoreTestimonials() {
        // Only show "view more" button in simplified view
        if (!this.isSimpilfiedUser) {
          return false
        }

        const allTestimonials = this.testimonials[this.activeTab] || []
        return allTestimonials.length > 3
      },
      showMoreButtonLabel() {
        const allTestimonials = this.testimonials[this.activeTab] || []
        const remainingCount = allTestimonials.length - 3
        const showMoreKey = `showMore${this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1)}`
        const showMore = this[showMoreKey]

        if (showMore) {
          return this.$t('show_less')
        } else {
          return this.$t('show_more_testimonials', { count: remainingCount })
        }
      },
      isActorClaimee() {
        return this.profile?.claims?.some(claim => claim.id === this.actor.id)
      },
      canSubmitEdit() {
        return this.editForm.content &&
          this.editForm.content.length >= 10 &&
          this.editForm.content.length <= 2000 &&
          this.isEditContentValid &&
          this.isEditRatingValid &&
          !this.editLoading
      },
      isEditContentValid() {
        if (!this.editForm.content) return false
        if (this.editForm.content.length < 10) return false
        if (this.editForm.content.length > 2000) return false
        if (this.editForm.content.trim().length < 10) return false

        // Check for basic profanity/inappropriate content patterns
        const inappropriatePatterns = [
          /\b(spam|fake|scam|fraud)\b/i,
          /\b(hate|violence|abuse)\b/i,
          /^(.)\1{9,}$/, // Repeated characters
          /^[^a-zA-Z]*$/ // Only special characters
        ]

        return !inappropriatePatterns.some(pattern => pattern.test(this.editForm.content))
      },
      isEditRatingValid() {
        // Rating is optional, but if provided must be 1-5
        return this.editForm.rating === null || (this.editForm.rating >= 1 && this.editForm.rating <= 5)
      },
      editContentValidationMessage() {
        if (!this.editForm.content) return ''
        if (this.editForm.content.length < 10) return this.$t('testimonial_content_too_short')
        if (this.editForm.content.length > 2000) return this.$t('testimonial_content_too_long')
        if (this.editForm.content.trim().length < 10) return this.$t('testimonial_content_mostly_whitespace')
        if (!this.isEditContentValid) return this.$t('testimonial_content_inappropriate')
        return ''
      },
      canSubmitResponse() {
        return this.responseForm.content &&
          this.responseForm.content.length <= 1000 &&
          !this.responseLoading
      }
    },
    async mounted() {
      await this.loadTestimonials()
    },
    watch: {
      // Watch for modal closing to reload testimonials
      '$store.state.ui.visibleModals': {
        handler(newModals, oldModals) {
          // If CREATE_TESTIMONIAL modal was just closed, reload testimonials
          if (oldModals?.includes(MODAL_IDS.CREATE_TESTIMONIAL) &&
              !newModals?.includes(MODAL_IDS.CREATE_TESTIMONIAL)) {
            this.loadTestimonials()
          }
        },
        deep: true
      }
    },
    methods: {
      async loadTestimonials() {
        this.loading = true
        try {
          // Let the backend handle both received and sent testimonials
          const response = await getTestimonialsByActor(this.actor.id)

          // Extract all testimonial data from response
          const receivedTestimonials = response.testimonials || []
          const sentTestimonials = response.sent_testimonials || []

          // Process received testimonials
          const allReceivedTestimonials = receivedTestimonials.map(testimonial => ({
            ...testimonial,
            isPending: !testimonial.approved && (this.isActorClaimee || this.isTestimonialOwner(testimonial))
          }))

          // Separate visible and hidden testimonials for claimees
          if (this.isActorClaimee) {
            this.testimonials.received = allReceivedTestimonials.filter(testimonial =>
              testimonial.visibleToSender && testimonial.visibleToReceiver
            )
            this.testimonials.hidden = allReceivedTestimonials.filter(testimonial =>
              !testimonial.visibleToSender || !testimonial.visibleToReceiver
            )
          } else {
            // For non-claimees, show visible testimonials AND user's own testimonials
            this.testimonials.received = allReceivedTestimonials.filter(testimonial =>
              (testimonial.visibleToSender && testimonial.visibleToReceiver) ||
              this.isTestimonialOwner(testimonial)
            )
            this.testimonials.hidden = []
          }

          // Process sent testimonials (already filtered by backend for this actor)
          this.testimonials.sent = sentTestimonials.map(testimonial => ({
            ...testimonial,
            isPending: !testimonial.approved
          }))

        } catch (error) {
          console.error('Failed to load testimonials:', error)
          this.testimonials = { received: [], sent: [], hidden: [] }
        } finally {
          this.loading = false
        }
      },

      setActiveTab(tab) {
        this.activeTab = tab
        // Reset show more state when switching tabs
        this.showMoreReceived = false
        this.showMoreSent = false
        this.showMoreHidden = false
      },

      toggleShowMore() {
        const showMoreKey = `showMore${this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1)}`
        this[showMoreKey] = !this[showMoreKey]
      },

      openTestimonialModal() {
        // Set modal context with target actor
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          targetActor: this.actor
        })
        // Show the modal
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_TESTIMONIAL)
      },

      formatDate(dateString) {
        if (!dateString) return ''

        const date = new Date(dateString)
        return date.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },

      // Permission checks
      canManageTestimonial(testimonial) {
        return testimonial.canManage || this.isTestimonialOwner(testimonial) || this.isTargetActorOwner(testimonial) || (testimonial.isPending && this.isTargetActorOwner(testimonial))
      },

      canEditTestimonial(testimonial) {
        return this.isTestimonialOwner(testimonial)
      },

      canRespondToTestimonial(testimonial) {
        return this.isTargetActorOwner(testimonial) && !testimonial.receiverResponse
      },

      getEmptyStateMessage() {
        switch (this.activeTab) {
          case 'received':
            return this.$t('no_received_testimonials')
          case 'sent':
            return this.$t('no_sent_testimonials')
          case 'hidden':
            return this.$t('no_hidden_testimonials')
          default:
            return this.$t('no_testimonials')
        }
      },

      isTestimonialOwner(testimonial) {
        return testimonial.user.id === this.profile?.id
      },

      isTargetActorOwner(testimonial) {
        // Since we're viewing an actor profile, check if user has claim on this actor
        // Use the same logic as isActorClaimee which is working
        return this.isActorClaimee
      },

      // Edit testimonial methods
      startEditTestimonial(testimonial) {
        this.editingTestimonial = testimonial
        this.editForm.content = testimonial.content
        this.editForm.rating = testimonial.rating
        this.editErrors = {}
      },

      setEditRating(rating) {
        this.editForm.rating = rating
      },

      cancelEdit() {
        this.editingTestimonial = null
        this.editForm = { content: '', rating: null }
        this.editErrors = {}
      },

      validateEditForm() {
        const errors = {}

        // Validate content
        if (!this.editForm.content || this.editForm.content.trim().length === 0) {
          errors.content = [this.$t('testimonial_content_required')]
        } else if (this.editForm.content.length < 10) {
          errors.content = [this.$t('testimonial_content_too_short')]
        } else if (this.editForm.content.length > 2000) {
          errors.content = [this.$t('testimonial_content_too_long')]
        } else if (this.editForm.content.trim().length < 10) {
          errors.content = [this.$t('testimonial_content_mostly_whitespace')]
        } else if (!this.isEditContentValid) {
          errors.content = [this.$t('testimonial_content_inappropriate')]
        }

        // Validate rating (optional)
        if (this.editForm.rating !== null && (this.editForm.rating < 1 || this.editForm.rating > 5)) {
          errors.rating = [this.$t('testimonial_rating_invalid')]
        }

        return errors
      },

      async submitEdit() {
        // Frontend validation
        const validationErrors = this.validateEditForm()
        if (Object.keys(validationErrors).length > 0) {
          this.editErrors = validationErrors
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: this.$t('testimonial_validation_failed')
          })
          return
        }

        this.editLoading = true
        this.editErrors = {}

        try {
          // Sanitize content before sending
          const sanitizedContent = this.editForm.content.trim()
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newline

          await updateTestimonial(this.actor.id, this.editingTestimonial.id, {
            content: sanitizedContent,
            rating: this.editForm.rating
          })

          this.cancelEdit()
          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_edit_success')
          })
        } catch (error) {
          if (error.response?.data?.errors) {
            this.editErrors = error.response.data.errors
          } else {
            this.$store.dispatch('showNotification', {
              type: 'error',
              message: error.response?.data?.message || this.$t('error_occurred')
            })
          }
        } finally {
          this.editLoading = false
        }
      },

      // Response methods
      startResponseToTestimonial(testimonial) {
        this.respondingToTestimonial = testimonial
        this.responseForm.content = ''
        this.responseErrors = {}
      },

      cancelResponse() {
        this.respondingToTestimonial = null
        this.responseForm = { content: '' }
        this.responseErrors = {}
      },

      async submitResponse() {
        this.responseLoading = true
        this.responseErrors = {}

        try {
          await addTestimonialResponse(this.actor.id, this.respondingToTestimonial.id, {
            response: this.responseForm.content
          })

          this.cancelResponse()
          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_response_success_reciprocal')
          })
        } catch (error) {
          if (error.response?.data?.errors) {
            this.responseErrors = error.response.data.errors
          } else {
            this.$store.dispatch('showNotification', {
              type: 'error',
              message: error.response?.data?.message || this.$t('error_occurred')
            })
          }
        } finally {
          this.responseLoading = false
        }
      },

      // Visibility methods
      async toggleTestimonialVisibility(testimonial) {
        try {
          const newVisibility = !testimonial.isVisible

          await updateTestimonialVisibility(this.actor.id, testimonial.id, {
            visible_to_sender: newVisibility,
            visible_to_receiver: newVisibility
          })

          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_visibility_updated')
          })
        } catch (error) {
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: error.response?.data?.message || this.$t('error_occurred')
          })
        }
      },

      // Delete methods
      confirmDeleteTestimonial(testimonial) {
        this.deletingTestimonial = testimonial
      },

      cancelDelete() {
        this.deletingTestimonial = null
      },

      async executeDelete() {
        this.deleteLoading = true

        try {
          await deleteTestimonial(this.actor.id, this.deletingTestimonial.id)

          this.cancelDelete()
          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_delete_success')
          })
        } catch (error) {
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: error.response?.data?.message || this.$t('error_occurred')
          })
        } finally {
          this.deleteLoading = false
        }
      },

      // Approval methods
      async approveTestimonial(testimonial) {
        try {
          await approveTestimonial(this.actor.id, testimonial.id)

          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_approved_success')
          })
        } catch (error) {
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: error.response?.data?.message || this.$t('error_occurred')
          })
        }
      },

      async rejectTestimonial(testimonial) {
        try {
          await rejectTestimonial(this.actor.id, testimonial.id)

          await this.loadTestimonials()

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: this.$t('testimonial_rejected_success')
          })
        } catch (error) {
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: error.response?.data?.message || this.$t('error_occurred')
          })
        }
      }
    },
  })
</script>

<style lang="scss" scoped>
  .actor-detail__testimonials {
    .testimonials-tabs {
      display: flex;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 20px;

      .tab-button {
        background: none;
        border: none;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
        color: #6b7280;
        cursor: pointer;
        border-bottom: 2px solid transparent;
        transition: all 0.2s ease;

        &:hover {
          color: #374151;
          background: #f9fafb;
        }

        &.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
          background: #f8faff;
        }
      }
    }

    .create-testimonial-form {
      margin: 20px 0;
      padding: 24px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;

      .collaboration-message {
        margin-bottom: 24px;
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 8px;
        color: white;

        .message-title {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 700;
          color: white;
        }

        .message-text {
          margin: 0;
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.95);
        }
      }

      .relationship-detected-banner {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        margin-bottom: 20px;
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        border-radius: 6px;
        color: #059669;
        font-size: 14px;

        strong {
          font-weight: 600;
        }
      }

      .testimonial-form {
        .form-row {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 20px;
          margin-bottom: 20px;

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        .form-group {
          .form-label {
            display: block;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-select,
          .form-input {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            background: white;
            transition: border-color 0.2s ease;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            }

            &.invalid {
              border-color: #ef4444;
              background-color: #fef2f2;
            }
          }

          .help-text {
            font-size: 12px;
            color: #6b7280;
            margin-top: 4px;

            &.relationship-detected {
              display: flex;
              align-items: center;
              gap: 4px;
              color: #059669;
              font-weight: 500;
              background: #ecfdf5;
              padding: 6px 8px;
              border-radius: 4px;
              border: 1px solid #a7f3d0;
            }
          }

          .no-connections-message {
            margin-top: 6px;
            font-size: 12px;
            color: #6b7280;
            font-style: italic;
            padding: 8px 12px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 4px;
          }

          .content-guidance {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
            line-height: 1.4;
          }

          .form-textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
            min-height: 100px;
            transition: border-color 0.2s ease;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            }

            &.invalid {
              border-color: #ef4444;
              background-color: #fef2f2;
            }
          }

          .textarea-footer {
            display: flex;
            justify-content: flex-end;
            margin-top: 6px;

            .character-count {
              font-size: 12px;
              color: #6b7280;

              &.over-limit {
                color: #dc2626;
                font-weight: 600;
              }
            }
          }

          .form-error {
            margin-top: 6px;
            font-size: 12px;
            color: #dc2626;
            font-weight: 500;
          }

          &.rating-group {
            .rating-input {
              display: flex;
              align-items: center;
              gap: 8px;

              .star-button {
                background: none;
                border: none;
                cursor: pointer;
                padding: 4px;
                color: #e5e7eb;
                font-size: 18px;
                transition: color 0.2s ease;

                &:hover,
                &.active {
                  color: #fbbf24;
                }
              }

              .clear-rating-btn {
                background: none;
                border: none;
                cursor: pointer;
                font-size: 12px;
                color: #6b7280;
                text-decoration: underline;
                margin-left: 8px;

                &:hover {
                  color: #374151;
                }
              }
            }
          }
        }

        .preview-section {
          margin: 20px 0;
          padding: 16px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;

          .preview-title {
            margin: 0 0 12px 0;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
          }

          .preview-content {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            padding: 16px;

            .preview-header {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 12px;
              flex-wrap: wrap;

              strong {
                font-weight: 600;
                color: #374151;
              }

              .preview-role {
                font-size: 12px;
                color: #6b7280;
                background: #f3f4f6;
                padding: 2px 6px;
                border-radius: 3px;
              }

              .preview-rating {
                display: flex;
                gap: 2px;
                margin-left: auto;

                .star-filled {
                  color: #fbbf24;
                  font-size: 14px;
                }

                .star-empty {
                  color: #e5e7eb;
                  font-size: 14px;
                }
              }
            }

            .preview-testimonial {
              p {
                margin: 0;
                font-size: 14px;
                line-height: 1.6;
                color: #374151;
                font-style: italic;
              }
            }
          }
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;

          @media (max-width: 480px) {
            flex-direction: column;
          }
        }
      }
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 40px 20px;
      color: #6b7280;
      font-size: 14px;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #6b7280;

      .empty-icon {
        font-size: 48px;
        color: #d1d5db;
        margin-bottom: 16px;
      }

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
      }
    }

    .testimonials-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 20px;
      margin-top: 20px;

      .testimonial-item {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        &:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        &.pending-approval {
          opacity: 0.6;
          background: #f9fafb;
          border-style: dashed;
          position: relative;

          &::after {
            content: 'PENDING APPROVAL';
            position: absolute;
            top: 8px;
            right: 8px;
            background: #f59e0b;
            color: white;
            font-size: 10px;
            font-weight: 600;
            padding: 2px 6px;
            border-radius: 3px;
            text-transform: uppercase;
            z-index: 1;
          }

          &:hover {
            transform: none;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
        }

        &.hidden-testimonial {
          opacity: 0.5;
          background: #f3f4f6;
          border: 2px solid #d1d5db;
          position: relative;

          &::after {
            content: 'HIDDEN';
            position: absolute;
            top: 8px;
            right: 8px;
            background: #6b7280;
            color: white;
            padding: 2px 6px;
            font-size: 10px;
            font-weight: 600;
            border-radius: 3px;
            text-transform: uppercase;
            z-index: 1;
          }

          .testimonial-content {
            opacity: 0.8;
          }
        }

        .testimonial-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 12px;

          .testimonial-author {
            flex: 1;
            min-width: 0;

            .author-company {
              margin: 0 0 4px 0;
              font-size: 16px;
              font-weight: 600;
              color: #374151;
              line-height: 1.2;
            }

            .author-user {
              font-size: 12px;
              color: #6b7280;
              font-weight: 500;
            }

            .relationship-description {
              font-size: 11px;
              color: #9ca3af;
              font-style: italic;
              display: block;
              margin-top: 2px;
            }
          }

          .testimonial-rating {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;

            .rating-stars {
              display: flex;
              gap: 2px;

              .star-filled {
                color: #fbbf24;
                font-size: 14px;
              }

              .star-empty {
                color: #e5e7eb;
                font-size: 14px;
              }
            }

            .rating-value {
              font-size: 12px;
              font-weight: 600;
              color: #6b7280;
            }
          }
        }

        .testimonial-content {
          margin-bottom: 16px;

          p {
            margin: 0;
            font-size: 14px;
            line-height: 1.6;
            color: #374151;
            font-style: italic;
          }
        }

        .testimonial-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;

          .testimonial-date {
            font-size: 12px;
            color: #9ca3af;
            font-weight: 500;
          }

          .testimonial-actions {
            display: flex;
            gap: 4px;

            .delete-btn {
              color: #dc2626;

              &:hover {
                background-color: rgba(220, 38, 38, 0.1);
              }
            }
          }
        }

        .edit-info {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          font-size: 11px;
          color: #6b7280;

          .edit-date {
            color: #9ca3af;
          }
        }

        .receiver-response {
          margin-top: 16px;
          padding: 12px;
          background: #f9fafb;
          border-left: 3px solid #e5e7eb;
          border-radius: 4px;

          .response-header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 8px;

            .response-label {
              font-size: 12px;
              font-weight: 600;
              color: #374151;
            }

            .response-date {
              font-size: 11px;
              color: #6b7280;
            }
          }

          .response-content {
            margin: 0;
            font-size: 13px;
            line-height: 1.5;
            color: #4b5563;
            font-style: italic;
          }
        }
      }
    }

    .show-more-container {
      display: flex;
      justify-content: center;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid #f3f4f6;

      .show-more-btn {
        min-width: 160px;
      }
    }
  }

  // Modal styles
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;

    .modal-content {
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 500px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);

      .modal-title {
        margin: 0 0 20px 0;
        font-size: 18px;
        font-weight: 600;
        color: #374151;
      }

      .edit-form,
      .response-form {
        .form-group {
          margin-bottom: 20px;

          .form-label {
            display: block;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
            min-height: 100px;
            transition: border-color 0.2s ease;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            }
          }

          .textarea-footer {
            display: flex;
            justify-content: flex-end;
            margin-top: 6px;

            .character-count {
              font-size: 12px;
              color: #6b7280;

              &.over-limit {
                color: #dc2626;
                font-weight: 600;
              }
            }
          }

          .form-error {
            margin-top: 6px;
            font-size: 12px;
            color: #dc2626;
            font-weight: 500;
          }

          .rating-input {
            display: flex;
            align-items: center;
            gap: 8px;

            .star-button {
              background: none;
              border: none;
              cursor: pointer;
              padding: 4px;
              color: #e5e7eb;
              font-size: 18px;
              transition: color 0.2s ease;

              &:hover,
              &.active {
                color: #fbbf24;
              }
            }

            .clear-rating-btn {
              background: none;
              border: none;
              cursor: pointer;
              font-size: 12px;
              color: #6b7280;
              text-decoration: underline;
              margin-left: 8px;

              &:hover {
                color: #374151;
              }
            }
          }
        }
      }

      .modal-actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid #e5e7eb;

        @media (max-width: 480px) {
          flex-direction: column;
        }
      }

      p {
        margin: 0 0 20px 0;
        font-size: 14px;
        line-height: 1.6;
        color: #374151;
      }
    }

    // Validation styles for the create form
    .create-testimonial-form {
      .textarea-footer {
        .validation-feedback {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;

          .validation-icon {
            font-size: 14px;
            flex-shrink: 0;

            &.valid {
              color: #10b981;
            }

            &.invalid {
              color: #ef4444;
            }
          }

          .validation-text {
            font-size: 12px;
            line-height: 1.3;

            &.valid {
              color: #10b981;
            }

            &.invalid {
              color: #ef4444;
            }
          }
        }

        .character-count {
          &.valid {
            color: #10b981;
            background: #f0fdf4;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .actor-detail__testimonials {
      .testimonials-grid {
        grid-template-columns: 1fr;
        gap: 16px;

        .testimonial-item {
          padding: 16px;

          .testimonial-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;

            .testimonial-rating {
              align-self: flex-start;
            }
          }
        }
      }
    }
  }
</style>
